/**
 * Mini-timeline orizzontale F30 — visualizza il ciclo di vita commerciale del preventivo:
 *   Creato → Inviato (sent_at) → Vinto/Perso (won_at/lost_at)
 *
 * Mostra le durate (Ngg in attesa risposta, Ngg in bozza) per dare percezione
 * di velocità del workflow. Step inattivi grigi, step attivi colorati per status.
 */
import React from 'react';
import { FileEdit, Send, Trophy, XCircle, Clock } from 'lucide-react';
import type { Estimate } from '@/types';

interface EstimateTimelineProps {
  estimate: Estimate;
}

interface StepDef {
  label: string;
  icon: React.ReactNode;
  date: Date | null | undefined;
  color: string;
  active: boolean;
}

function daysBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function fmtDate(d: Date | null | undefined): string {
  if (!d) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

const EstimateTimeline: React.FC<EstimateTimelineProps> = ({ estimate }) => {
  const status = estimate.status;
  const isWon = status === 'won' || status === 'contracted';
  const isLost = status === 'lost';
  const isSent = status === 'sent' || status === 'pending';
  const isClosed = isWon || isLost;
  const today = new Date();

  // Step 1: Creato (sempre)
  // Step 2: Inviato (se sent_at o status passato)
  // Step 3: Vinto/Perso (se won_at/lost_at)
  const steps: StepDef[] = [
    {
      label: 'Creato',
      icon: <FileEdit className="h-4 w-4" />,
      date: estimate.createdAt,
      color: 'bg-slate-100 text-slate-700 border-slate-300',
      active: true,
    },
    {
      label: 'Inviato',
      icon: <Send className="h-4 w-4" />,
      date: estimate.sentAt,
      color: estimate.sentAt || isClosed
        ? 'bg-amber-100 text-amber-800 border-amber-300'
        : 'bg-muted/30 text-muted-foreground border-muted',
      active: !!estimate.sentAt || isClosed || isSent,
    },
    isLost
      ? {
        label: 'Perso',
        icon: <XCircle className="h-4 w-4" />,
        date: estimate.lostAt,
        color: 'bg-red-100 text-red-800 border-red-300',
        active: true,
      }
      : {
        label: isWon ? 'Vinto' : 'Vinto / Perso',
        icon: <Trophy className="h-4 w-4" />,
        date: estimate.wonAt,
        color: isWon
          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
          : 'bg-muted/30 text-muted-foreground border-muted',
        active: isWon,
      },
  ];

  // Calcolo durate tra step
  const sentDate = estimate.sentAt ?? null;
  const closedDate = estimate.wonAt ?? estimate.lostAt ?? null;
  const draftDays = sentDate ? daysBetween(estimate.createdAt, sentDate) : daysBetween(estimate.createdAt, today);
  const responseDays = sentDate && closedDate ? daysBetween(sentDate, closedDate) : sentDate ? daysBetween(sentDate, today) : null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-medium">Cronologia</h4>
      </div>
      <div className="flex items-stretch gap-2 overflow-x-auto">
        {steps.map((step, idx) => (
          <React.Fragment key={step.label}>
            <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-md border min-w-[100px] ${step.color}`}>
              <div className="mb-1">{step.icon}</div>
              <div className="text-xs font-medium">{step.label}</div>
              <div className="text-[10px] text-muted-foreground/80 mt-1">{fmtDate(step.date)}</div>
            </div>
            {idx < steps.length - 1 && (
              <div className="flex-1 flex flex-col items-center justify-center min-w-[60px]">
                {idx === 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    {sentDate ? `${draftDays} gg` : `${draftDays} gg fa…`}
                  </span>
                )}
                {idx === 1 && responseDays !== null && (
                  <span className="text-[10px] text-muted-foreground">
                    {closedDate ? `${responseDays} gg` : `${responseDays} gg in attesa`}
                  </span>
                )}
                <div className={`h-0.5 w-full mt-1 ${steps[idx + 1].active ? 'bg-primary/40' : 'bg-muted'}`} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default EstimateTimeline;
